import React from "react";
import DatePicker from "react-datepicker";
import { useFormikContext } from "formik";
import getUserLocale from "get-user-locale";
import calendarIcon from "assets/Icon_Action_06.png";
import { DateTypes, handleDateChange, debouncedHandleDateChange, removeDate } from "../../../Task";
import { HeaderTaskDateFieldsProps } from "../GatewaySummary.types";
import { useDateParsing } from "../GatewaySummary.hooks";
import { useOptionalGatewayChange } from "../../../../context";

/**
 * HeaderTaskDateFields component renders the Planned, Forecast, and Actual date inputs
 * These are displayed in the header task milestone section
 */
export const HeaderTaskDateFields: React.FC<HeaderTaskDateFieldsProps> = ({
  task,
  bEditable,
  projectID,
  gatewaysID,
  gatewayTemplateID,
  gatewayIndex,
  taskIndex,
  gatewayData,
  setGatewayData,
  backgroundReloadGatewayDataToggle,
  setBackgroundReloadGatewayDataToggle,
}) => {
  const { parsedPlannedDate, parsedForecastDate, parsedActualDate } = useDateParsing(task);
  const locale = getUserLocale();
  const formik = useFormikContext();
  const gatewayChange = useOptionalGatewayChange();

  // Callback to mark that gateway data has changed
  const handleChangeSuccess = () => {
    gatewayChange?.setHasChanges(true);
  };

  return (
    <div className={`Dates ${task.bComplete ? "Complete" : ""}`}>
      {/* Planned Date Field */}
      {task.bPlannedActive ? (
        <div className="RequiredDate DateContainer">
          <div
            className={`Date ${task.bPlannedReadOnly || !bEditable ? "Disabled" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="Text">Planned:</div>

            <DatePicker
              dateFormat="dd-MMM-yyyy"
              locale={locale}
              onChange={(e) =>
                debouncedHandleDateChange(
                  e,
                  DateTypes.PLANNED,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  gatewaysID,
                  gatewayTemplateID,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  handleChangeSuccess
                )
              }
              selected={parsedPlannedDate ?? null}
            />
            {task.plannedDate &&
              removeDate(() =>
                handleDateChange(
                  null,
                  DateTypes.PLANNED,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  gatewaysID,
                  gatewayTemplateID,
                  undefined,
                  handleChangeSuccess
                )
              )}
            <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
          </div>
        </div>
      ) : (
        <div className="RequiredDate DateContainer"></div>
      )}

      {/* Forecast Date Field */}
      {task.bForecastActive ? (
        <div className="ProjectedDate DateContainer">
          <div
            className={`Date ${task.bForecastReadOnly || !bEditable ? "Disabled" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="Text">Forecast:</div>

            <DatePicker
              dateFormat="dd-MMM-yyyy"
              locale={locale}
              onChange={(e) =>
                debouncedHandleDateChange(
                  e,
                  DateTypes.FORECAST,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  gatewaysID,
                  gatewayTemplateID,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  handleChangeSuccess
                )
              }
              selected={parsedForecastDate ?? null}
            />
            {task.forecastDate &&
              removeDate(() =>
                handleDateChange(
                  null,
                  DateTypes.FORECAST,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  gatewaysID,
                  gatewayTemplateID,
                  undefined,
                  handleChangeSuccess
                )
              )}
            <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
          </div>
        </div>
      ) : (
        <div className="ProjectedDate DateContainer"></div>
      )}

      {/* Actual Date Field */}
      {task.bActualActive ? (
        <div className={`CompletionDate DateContainer `}>
          <div
            className={`Date ${task.bActualReadOnly || !bEditable ? "Disabled" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="Text">Actual:</div>

            <DatePicker
              dateFormat="dd-MMM-yyyy"
              locale={locale}
              className={task.bActualReadOnly ? "locked" : ""}
              onChange={(e) =>
                debouncedHandleDateChange(
                  e,
                  DateTypes.ACTUAL,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  gatewaysID,
                  gatewayTemplateID,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  handleChangeSuccess
                )
              }
              selected={parsedActualDate ?? null}
            />
            {task.actualDate &&
              removeDate(() =>
                handleDateChange(
                  null,
                  DateTypes.ACTUAL,
                  formik,
                  gatewayIndex,
                  taskIndex,
                  task,
                  gatewayData,
                  setGatewayData,
                  projectID,
                  backgroundReloadGatewayDataToggle,
                  setBackgroundReloadGatewayDataToggle,
                  gatewaysID,
                  gatewayTemplateID,
                  undefined,
                  handleChangeSuccess
                )
              )}
            <img src={calendarIcon} alt="calendar icon" className="CalendarIcon" />
          </div>
        </div>
      ) : (
        <div className="CompletionDate DateContainer"></div>
      )}
    </div>
  );
};
